import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import type { MbBatchSize, MbRunSummary } from "#shared/schemas/mbEnrich.ts";
import { Button } from "@web/components/ui/button.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { EmptyState } from "@web/components/EmptyState.tsx";
import { usePlaylists } from "@web/features/library/api.ts";
import { coyLog } from "@web/lib/coyLog.ts";
import { cn } from "@web/lib/utils.ts";
import { CoverageDashboard } from "./CoverageDashboard.tsx";
import { formatTrackPulse } from "./enrichProgress.ts";
import { JsonTree } from "./JsonTree.tsx";
import {
  useMbEntity,
  useMbStatus,
  usePauseMbPass,
  useResumeMbPass,
  useSaveMbContact,
  useStartMbBatch,
  useStopMbPass,
  useWipeFetchedMb,
} from "./mbApi.ts";

const SIZES: MbBatchSize[] = [5, 10, 25, 100, 500, 1000, 2000, 3000, 4000, "all"];

function formatDuration(sec: number): string {
  if (sec < 60) return `~${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return s > 0 ? `~${m}m ${s}s` : `~${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm > 0 ? `~${h}h ${rm}m` : `~${h}h`;
}

function formatElapsed(ms: number): string {
  const sec = Math.max(0, Math.round(ms / 1000));
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm > 0 ? `${h}h ${rm}m` : `${h}h`;
}

/** ETA for remaining work — same units as elapsed, with a leading ~. */
function formatEta(sec: number | null): string {
  if (sec == null || !Number.isFinite(sec) || sec < 0) return "—";
  if (sec < 15) return "<15s";
  return formatDuration(Math.round(sec));
}

function mbUrl(type: string, mbid: string): string {
  return `https://musicbrainz.org/${type}/${mbid}`;
}

function SummaryBlock({
  summary,
  playlistId,
  playlistName,
  onOpen,
  onViewCoverage,
}: {
  summary: MbRunSummary;
  playlistId: string | null | undefined;
  playlistName: string | null;
  onOpen: (mbid: string) => void;
  onViewCoverage: () => void;
}) {
  return (
    <section className="space-y-4 rounded-lg border border-line bg-panel px-4 py-4">
      <div>
        <h3 className="text-sm font-semibold text-text">Run complete</h3>
        <p className="mt-1 text-sm text-muted">
          {summary.tracks_processed.toLocaleString()} tracks ·{" "}
          {summary.requests_made.toLocaleString()} requests ·{" "}
          {formatElapsed(summary.elapsed_ms)}
        </p>
        {playlistId && (
          <p className="mt-2 text-sm">
            <button
              type="button"
              className="text-accent hover:underline"
              onClick={onViewCoverage}
            >
              View coverage
              {playlistName ? ` for ${playlistName}` : " for this playlist"}
            </button>
          </p>
        )}
      </div>

      <div className="space-y-1">
        {summary.by_type.map((row) => (
          <div key={row.entity_type} className="flex justify-between gap-3 text-sm">
            <span className="text-muted capitalize">{row.entity_type.replace("-", " ")}</span>
            <span className="tabular-nums text-text">
              {row.new_count.toLocaleString()} new
              {row.partial_count > 0 ? ` (${row.partial_count} partial)` : ""}
            </span>
          </div>
        ))}
      </div>

      {summary.field_coverage.length > 0 && (
        <div className="space-y-1 border-t border-line pt-3">
          <p className="text-xs font-semibold tracking-wider text-faint uppercase">
            Fields populated across new entities
          </p>
          {summary.field_coverage.map((row) => (
            <div key={row.label} className="flex justify-between gap-3 text-sm">
              <span className="text-muted">{row.label}</span>
              <span className="tabular-nums text-text">
                {row.have} / {row.of}
              </span>
            </div>
          ))}
        </div>
      )}

      {summary.acousticbrainz && summary.acousticbrainz.fetched > 0 && (
        <div className="space-y-1 border-t border-line pt-3">
          <p className="text-xs font-semibold tracking-wider text-faint uppercase">
            AcousticBrainz
          </p>
          <div className="flex justify-between gap-3 text-sm">
            <span className="text-muted">Found</span>
            <span className="tabular-nums text-text">
              {summary.acousticbrainz.found.toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between gap-3 text-sm">
            <span className="text-muted">Not in archive</span>
            <span className="tabular-nums text-text">
              {summary.acousticbrainz.not_found.toLocaleString()}
            </span>
          </div>
          {summary.acousticbrainz.failed > 0 && (
            <div className="flex justify-between gap-3 text-sm">
              <span className="text-muted">Failed</span>
              <span className="tabular-nums text-text">
                {summary.acousticbrainz.failed.toLocaleString()}
              </span>
            </div>
          )}
        </div>
      )}

      {summary.new_mbids.length > 0 && (
        <div className="space-y-1 border-t border-line pt-3">
          <p className="text-xs font-semibold tracking-wider text-faint uppercase">
            New entities (click for raw JSON)
          </p>
          <div className="max-h-64 space-y-1 overflow-y-auto">
            {summary.new_mbids.map((e) => (
              <button
                key={e.mbid}
                type="button"
                className="flex w-full items-baseline justify-between gap-2 rounded px-1 py-1 text-left text-sm hover:bg-panel-elevated"
                onClick={() => onOpen(e.mbid)}
              >
                <span className="min-w-0 truncate text-accent">
                  {e.name ?? e.mbid.slice(0, 8)}
                </span>
                <span className="shrink-0 text-xs text-faint">
                  {e.entity_type} · {e.fetch_status}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function RawViewer({ mbid, onClose }: { mbid: string; onClose: () => void }) {
  const q = useMbEntity(mbid);
  const entity = q.data?.entity;
  return (
    <section className="space-y-3 rounded-lg border border-accent/40 bg-panel-elevated px-4 py-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold tracking-wider text-faint uppercase">Raw response</p>
          <p className="mt-1 truncate font-mono text-xs text-muted">{mbid}</p>
          {entity && (
            <a
              href={mbUrl(entity.entity_type, mbid)}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-block text-sm text-accent hover:underline"
            >
              Open on musicbrainz.org
            </a>
          )}
        </div>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>
      {q.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : entity ? (
        <div className="max-h-96 overflow-auto rounded-md border border-line bg-panel px-3 py-2">
          <JsonTree value={entity.raw_json} name={entity.entity_type} />
        </div>
      ) : (
        <p className="text-sm text-muted">Entity not found in store.</p>
      )}
    </section>
  );
}

/** Workbench → Enrich: coverage dashboard + batch cascade (catalog or playlist). */
export function EnrichPanel() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const coveragePlaylistId = params.get("playlist") || null;

  const [enrichScope, setEnrichScope] = useState<"catalog" | "playlist">("catalog");
  const [enrichPlaylistId, setEnrichPlaylistId] = useState<string>("");

  const statusQ = useMbStatus(
    enrichScope === "playlist" ? { playlistId: enrichPlaylistId || null } : null,
    {
    refetchInterval: (q) => {
      const run = q.state.data?.run;
      return run?.status === "running" || run?.status === "paused" ? 1000 : 8_000;
    },
    },
  );

  const playlistsQ = usePlaylists();
  const playlists = useMemo(
    () =>
      [...(playlistsQ.data ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      ),
    [playlistsQ.data],
  );

  const saveContact = useSaveMbContact();
  const startBatch = useStartMbBatch();
  const pausePass = usePauseMbPass();
  const resumePass = useResumeMbPass();
  const stopPass = useStopMbPass();
  const wipeFetched = useWipeFetchedMb();

  const [size, setSize] = useState<MbBatchSize>(10);
  const [forceRefetch, setForceRefetch] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState(false);
  const [contactDraft, setContactDraft] = useState("");
  const [viewMbid, setViewMbid] = useState<string | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const coverageRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (statusQ.data?.settings.contact) setContactDraft(statusQ.data.settings.contact);
  }, [statusQ.data?.settings.contact]);

  // Prefer the playlist that just finished when picking defaults.
  useEffect(() => {
    const runPl = statusQ.data?.run?.playlist_id;
    if (runPl && !enrichPlaylistId && playlists.some((p) => p.id === runPl)) {
      setEnrichPlaylistId(runPl);
    }
  }, [statusQ.data?.run?.playlist_id, enrichPlaylistId, playlists]);

  const status = statusQ.data;
  const run = status?.run ?? null;
  const active = run?.status === "running" || run?.status === "paused";

  // Refresh coverage tables when a run finishes.
  const lastDoneFp = useRef<string | null>(null);
  useEffect(() => {
    if (!run || (run.status !== "done" && run.status !== "stopped")) return;
    const fp = `${run.status}|${run.finished_at ?? run.updated_at}|${run.tracks_done}`;
    if (fp === lastDoneFp.current) return;
    lastDoneFp.current = fp;
    void qc.invalidateQueries({ queryKey: ["mb-coverage"] });
  }, [run, qc]);

  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);

  const lastFp = useRef<string | null>(null);
  useEffect(() => {
    if (!run) return;
    const fp = `${run.status}|${run.tracks_done}|${run.requests_made}|${run.current_url ?? ""}`;
    if (fp === lastFp.current) return;
    lastFp.current = fp;
    coyLog.info(`mb enrich · ${run.status}`, {
      mode: run.mode,
      playlist: run.playlist_id,
      tracks: `${run.tracks_done}/${run.tracks_total}`,
      requests: run.requests_made,
      queue: run.queue_depth,
      url: run.current_url,
      error: run.error,
    });
  }, [run]);

  const estimate = status?.estimates[String(size)];
  const live = useMemo(() => {
    if (!run || run.tracks_total <= 0) {
      return {
        pct: 0,
        pctLabel: "0%",
        elapsedLabel: "—",
        etaLabel: "—",
        rateLabel: "—",
        remaining: 0,
      };
    }
    void nowTick;
    const started = Date.parse(run.started_at);
    const elapsedMs = Number.isFinite(started)
      ? Math.max(0, Date.now() - started)
      : 0;
    const pctRaw = (run.tracks_done / run.tracks_total) * 100;
    const pct = Math.min(100, Math.round(pctRaw * 10) / 10);
    const remaining = Math.max(0, run.tracks_total - run.tracks_done);
    const elapsedSec = elapsedMs / 1000;
    const tracksPerSec =
      run.tracks_done >= 2 && elapsedSec > 0 ? run.tracks_done / elapsedSec : 0;
    const etaSec =
      run.status === "paused"
        ? null
        : tracksPerSec > 0
          ? remaining / tracksPerSec
          : null;
    const reqPerMin =
      elapsedSec > 0 ? (run.requests_made / elapsedSec) * 60 : 0;

    return {
      pct,
      pctLabel: `${pct.toFixed(pct % 1 === 0 ? 0 : 1)}%`,
      elapsedLabel: formatElapsed(elapsedMs),
      etaLabel:
        run.status === "paused"
          ? "paused"
          : remaining === 0
            ? "done"
            : formatEta(etaSec),
      rateLabel:
        tracksPerSec > 0
          ? `${(tracksPerSec * 60).toFixed(1)} tracks/min · ${reqPerMin.toFixed(0)} req/min`
          : run.tracks_done === 0
            ? "warming up…"
            : "—",
      remaining,
    };
  }, [run, nowTick]);

  function setCoveragePlaylist(id: string | null) {
    const copy = new URLSearchParams(params);
    copy.set("tab", "enrich");
    if (id) copy.set("playlist", id);
    else copy.delete("playlist");
    setParams(copy, { replace: true });
  }

  function viewRunCoverage() {
    const id = run?.playlist_id ?? null;
    if (id) setCoveragePlaylist(id);
    requestAnimationFrame(() => {
      coverageRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  const runPlaylistName =
    (run?.playlist_id && playlists.find((p) => p.id === run.playlist_id)?.name) ||
    null;

  if (statusQ.isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (statusQ.isError || !status) {
    return (
      <EmptyState
        title="Could not load enrich status"
        description={statusQ.error instanceof Error ? statusQ.error.message : "Try again."}
      />
    );
  }

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <h2 className="text-sm font-semibold tracking-wide text-text">Enrich</h2>
        <p className="text-sm text-muted">
          One cascade per track: ISRC → harvest MBIDs → artist / release / work lookups
          (MusicBrainz 1 req/s). AcousticBrainz low+high start as soon as the recording
          MBID exists and run in parallel with that cascade. Stopping keeps everything
          already stored.
        </p>

        {active && run && (
          <div className="space-y-3 rounded-lg border border-accent/40 bg-panel-elevated px-4 py-4">
            <div className="flex items-center justify-between gap-3">
              <p className="shrink-0 text-xs font-semibold tracking-wider text-faint uppercase">
                {run.mode}
                {run.playlist_id ? " · playlist" : ""} · {run.status}
              </p>
              <div className="flex shrink-0 gap-2">
                {run.status === "running" ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={pausePass.isPending}
                    onClick={() => pausePass.mutate()}
                  >
                    Pause
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={resumePass.isPending}
                    onClick={() => resumePass.mutate()}
                  >
                    Resume
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={stopPass.isPending}
                  onClick={() => stopPass.mutate()}
                >
                  Stop
                </Button>
              </div>
            </div>

            <p
              className="h-4 truncate font-mono text-xs leading-4 text-muted"
              title={run.current_url ?? undefined}
            >
              {run.current_url?.trim() ? run.current_url : "Waiting for next request…"}
            </p>

            <div className="flex items-baseline justify-between gap-3">
              <p className="shrink-0 text-lg font-semibold tabular-nums leading-7 text-text">
                {live.pctLabel}
              </p>
              <p className="min-w-0 truncate text-right text-sm tabular-nums leading-7 text-muted">
                {run.tracks_done.toLocaleString()} / {run.tracks_total.toLocaleString()} tracks
                {" · "}
                {live.remaining.toLocaleString()} left
              </p>
            </div>

            <div
              className="h-2 w-full overflow-hidden rounded-full bg-panel"
              role="progressbar"
              aria-valuenow={live.pct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className={cn(
                  "h-full rounded-full bg-accent transition-[width] duration-500",
                  run.status === "running" && "animate-pulse",
                )}
                style={{ width: `${Math.min(100, Math.max(0, live.pct))}%` }}
              />
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
              <div className="flex min-w-0 items-baseline justify-between gap-2">
                <span className="shrink-0 text-muted">Elapsed</span>
                <span className="truncate tabular-nums text-text">{live.elapsedLabel}</span>
              </div>
              <div className="flex min-w-0 items-baseline justify-between gap-2">
                <span className="shrink-0 text-muted">Est. left</span>
                <span className="truncate tabular-nums text-text">{live.etaLabel}</span>
              </div>
              <div className="col-span-2 flex min-w-0 items-baseline justify-between gap-2">
                <span className="shrink-0 text-muted">Pace</span>
                <span className="truncate tabular-nums text-right text-text">{live.rateLabel}</span>
              </div>
              <div className="col-span-2 flex min-w-0 items-baseline justify-between gap-2">
                <span className="shrink-0 text-muted">Current</span>
                <span className="truncate text-right text-text">
                  {run.current_track?.trim() || "—"}
                </span>
              </div>
              <div className="col-span-2 flex min-w-0 items-baseline justify-between gap-2">
                <span className="shrink-0 text-muted">Step</span>
                <span className="truncate text-right text-text">
                  {run.current_step?.trim() || "—"}
                </span>
              </div>
              <div className="col-span-2 flex min-w-0 items-baseline justify-between gap-2">
                <span className="shrink-0 text-muted">Requests</span>
                <span className="truncate tabular-nums text-right text-text">
                  {run.requests_made.toLocaleString()} · queue {run.queue_depth}
                </span>
              </div>
              {run.last_track && (
                <div className="col-span-2 space-y-0.5 border-t border-line pt-1.5">
                  <div className="flex min-w-0 items-baseline justify-between gap-2">
                    <span className="shrink-0 text-muted">Last track</span>
                    <span className="truncate text-right text-text">
                      {run.last_track.name ?? "—"}
                    </span>
                  </div>
                  <p className="truncate text-right text-xs text-faint">
                    found {formatTrackPulse(run.last_track)}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {!active && (
          <div className="space-y-4 rounded-lg border border-line bg-panel px-4 py-4">
            <div className="space-y-2">
              <p className="text-xs text-muted">Enrich</p>
              <label className="flex items-center gap-2 text-sm text-text">
                <input
                  type="radio"
                  name="enrich-scope"
                  checked={enrichScope === "catalog"}
                  onChange={() => setEnrichScope("catalog")}
                />
                Whole shared catalog (unenriched ISRC tracks)
              </label>
              <label className="flex flex-wrap items-center gap-2 text-sm text-text">
                <input
                  type="radio"
                  name="enrich-scope"
                  checked={enrichScope === "playlist"}
                  onChange={() => setEnrichScope("playlist")}
                />
                <span>Playlist:</span>
                <select
                  value={enrichPlaylistId}
                  disabled={enrichScope !== "playlist"}
                  onChange={(e) => {
                    setEnrichPlaylistId(e.target.value);
                    setEnrichScope("playlist");
                  }}
                  className="rounded-md border border-line bg-panel-elevated px-3 py-1.5 text-sm text-text disabled:opacity-50"
                >
                  <option value="">Select playlist…</option>
                  {playlists.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <label className="space-y-1">
                <span className="text-xs text-muted">Fetch</span>
                <select
                  value={String(size)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSize(v === "all" ? "all" : (Number(v) as MbBatchSize));
                  }}
                  className="block rounded-md border border-line bg-panel-elevated px-3 py-2 text-sm text-text"
                >
                  {SIZES.map((s) => (
                    <option key={String(s)} value={String(s)}>
                      {s === "all" ? "All" : s.toLocaleString()}
                    </option>
                  ))}
                </select>
              </label>
              <Button
                disabled={
                  startBatch.isPending ||
                  (!forceRefetch && (!estimate || estimate.tracks === 0)) ||
                  status.settings.contact_source === "missing" ||
                  (enrichScope === "playlist" && !enrichPlaylistId)
                }
                onClick={() =>
                  startBatch.mutate({
                    size,
                    playlist_id:
                      enrichScope === "playlist" ? enrichPlaylistId || null : null,
                    force: forceRefetch,
                  })
                }
              >
                Run
              </Button>
              <label className="flex items-center gap-2 text-sm text-text">
                <input
                  type="checkbox"
                  checked={forceRefetch}
                  onChange={(e) => setForceRefetch(e.target.checked)}
                />
                Force refetch
              </label>
              {estimate && !forceRefetch && (
                <p className="w-full text-xs text-muted">
                  ~{estimate.tracks} tracks → est. {estimate.requests_min}–
                  {estimate.requests_max} requests → {formatDuration(estimate.time_sec_min)}–
                  {formatDuration(estimate.time_sec_max)}
                  {status.unenriched_tracks > 0 && (
                    <>
                      {" "}
                      · {status.unenriched_tracks.toLocaleString()} unenriched in scope
                    </>
                  )}
                </p>
              )}
              {forceRefetch && (
                <p className="w-full text-xs text-muted">
                  Re-fetches already-stored MBIDs — “already stored” will not skip work.
                </p>
              )}
              <p className="w-full text-xs text-faint">
                {enrichScope === "playlist"
                  ? "Order: playlist date added (newest first). Results write to the shared catalog."
                  : "Order: any user’s liked songs (newest first), then other ISRC tracks in the catalog. Results are shared."}
              </p>
            </div>
          </div>
        )}

        {run && !active && (run.status === "stopped" || run.status === "error") && (
          <div className="flex flex-wrap items-center gap-3">
            {run.error && <p className="text-sm text-status-cancelled">{run.error}</p>}
            {run.cursor_track_id && (
              <Button
                size="sm"
                variant="secondary"
                disabled={resumePass.isPending}
                onClick={() => resumePass.mutate()}
              >
                Resume from last cursor
              </Button>
            )}
          </div>
        )}
      </section>

      <div ref={coverageRef}>
        <CoverageDashboard
          playlistId={coveragePlaylistId}
          onPlaylistChange={setCoveragePlaylist}
          playlists={playlists.map((p) => ({ id: p.id, name: p.name }))}
        />
      </div>

      {run?.summary && !active && (
        <SummaryBlock
          summary={run.summary}
          playlistId={run.playlist_id}
          playlistName={runPlaylistName}
          onOpen={setViewMbid}
          onViewCoverage={viewRunCoverage}
        />
      )}

      {viewMbid && <RawViewer mbid={viewMbid} onClose={() => setViewMbid(null)} />}

      <section className="space-y-3">
        <h2 className="text-sm font-semibold tracking-wide text-text">MusicBrainz contact</h2>
        <p className="text-sm text-muted">
          Required in the User-Agent. Refuse to run if empty. Override or use{" "}
          <code className="text-xs">MUSICBRAINZ_CONTACT</code>.
        </p>
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            value={contactDraft}
            onChange={(e) => setContactDraft(e.target.value)}
            placeholder="you@example.com"
            className="min-w-[16rem] flex-1 rounded-md border border-line bg-panel px-3 py-2 text-sm text-text outline-none focus:border-accent"
          />
          <Button
            disabled={saveContact.isPending || contactDraft.trim().length < 3}
            onClick={() => saveContact.mutate(contactDraft.trim())}
          >
            Save
          </Button>
        </div>
        <p className="text-xs text-faint">Source: {status.settings.contact_source}</p>
      </section>

      <section className="space-y-3 rounded-lg border border-line bg-panel px-4 py-4">
        <h3 className="text-sm font-semibold text-text">Reset fetched data</h3>
        <p className="text-xs text-muted">
          Drops all <code className="text-xs">mb_entities</code>, auto-linked{" "}
          <code className="text-xs">mb_links</code>, and AcousticBrainz rows. Keeps manually
          locked MBIDs and the API request log.
        </p>
        {!wipeConfirm ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={active || wipeFetched.isPending}
            onClick={() => setWipeConfirm(true)}
          >
            Wipe fetched enrichment…
          </Button>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              disabled={active || wipeFetched.isPending}
              onClick={() => {
                wipeFetched.mutate(undefined, {
                  onSettled: () => setWipeConfirm(false),
                });
              }}
            >
              Confirm wipe
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setWipeConfirm(false)}>
              Cancel
            </Button>
          </div>
        )}
      </section>

      <p className="text-xs text-faint">
        Disk log: <code className="text-xs">data/logs/musicbrainz-api.jsonl</code> · F12{" "}
        <code className="text-xs">coykoi:mb</code>
      </p>
    </div>
  );
}
